import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AnimationShell, Button, Link, Typography } from '@/components';
import { Route } from '@/constants';
import {
  PasswordForm,
  PasswordFormFields,
  useAcceptInvitationMutation,
  useLogin,
  useRequestInviteTokenMutation,
  useTrackInviteIpMutation,
  useUpdateMeMutation,
} from '@/features/auth';
import { useRegisterGAPageView, useTokenParam } from '@/hooks';
import { jwtDecode } from 'jwt-decode';
import { BASE_URL } from '@/config';
import { useNotifyOnboardingCompleteMakeMutation } from '@/features/users/api/integrations';

export function SignUpInvitationPage() {
  const { t } = useTranslation(['signup']);

  const navigate = useNavigate();
  useRegisterGAPageView('Sign up invitation');

  const [mutation] = useAcceptInvitationMutation();
  const [trackInviteIp] = useTrackInviteIpMutation();
  const [updateMe] = useUpdateMeMutation();
  const [requestInviteToken, { isLoading, isError, isSuccess }] = useRequestInviteTokenMutation();
  const [notifyOnboardingComplete] = useNotifyOnboardingCompleteMakeMutation();

  const login = useLogin();
  const token = useTokenParam();

  let decodeToken: any = null;
  let isExpired = false;
  let email: string | null = null;

  try {
    decodeToken = jwtDecode(token);
    isExpired = decodeToken?.exp < Math.floor(Date.now() / 1000);
    email = 'email' in decodeToken ? (decodeToken.email as string) : null;
  } catch (error) {
    isExpired = true;
  }

  const onSubmitForm = useCallback(
    ({ password, phone_number }: PasswordFormFields) => {
      if (!token || !email) return;

      mutation({ token, password })
        .unwrap()
        .then(async () => {
          trackInviteIp({ email });
          await login({ email, password });
          if (phone_number) {
            await updateMe({ phone_number }).unwrap().catch(() => {});
          }
          navigate(Route.Home);
        })
        .catch(() => {
          navigate(Route.SignIn);
        });
    },
    [token, mutation, navigate, login, email, updateMe, trackInviteIp]
  );

  const handleRequestNewToken = async () => {
    if (!email) return;

    await requestInviteToken({
      email,
      url: `${BASE_URL}${Route.InvitationSignUp}`,
    })
      .unwrap()
      .then()
      .catch((error) => console.error('Error requesting token:', error));
  };

  if (isExpired) {
    return (
      <div className="w-full">
        <Typography as="h1" size="heading-2" color="primary" weight="semibold" className="mb-3 text-center">
          {!isSuccess ? t('Your link has expired') : t('A new invitation link has been sent to your email')}
        </Typography>
        <Typography size="body-2" color="secondary" className="text-center">
          {!isSuccess
            ? t('Please request a new email with invitation link.')
            : t('Please check it and verify your account.')}
        </Typography>

        {isError && (
          <Typography weight="normal" size="body-2" className="mt-6 text-center">
            {t('Something went wrong. Please contact us at')}{' '}
            <Link to="mailto:clients@nteaser.com">clients@nteaser.com</Link>
          </Typography>
        )}

        {!isSuccess && !isError && (
          <AnimationShell
            type="opacity"
            delay={0.35}
            className="mt-10 inline-flex w-full items-center justify-center gap-x-1"
          >
            <Button onClick={handleRequestNewToken} disabled={isLoading}>
              {t('Request a new link')}
            </Button>
          </AnimationShell>
        )}
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      <Typography as="h1" size="heading-1" color="primary" weight="semibold">
        {t(`Create your account`)}
      </Typography>
      <Typography as="p" size="body-1" color="primary" weight="semibold">
        {email}
      </Typography>

      <PasswordForm onSubmit={onSubmitForm} withPhone />

      <AnimationShell type="opacity" delay={0.35} className="inline-flex w-full items-center justify-center gap-x-1">
        <Typography weight="normal" size="body-2">
          {t(`Already have an account?`)}
        </Typography>
        <Link to={Route.SignIn}>{t(`Sign in`)}</Link>
      </AnimationShell>
    </div>
  );
}
